/**
 * The campaign composer: content, audience and proofing for one draft.
 *
 * Three panels rather than one long form, because the three questions a sender
 * actually asks — what does it say, who gets it, does it look right — are
 * answered with different information on screen, and a single scroll would bury
 * the audience count that makes the send decision.
 *
 * The HTML preview is rendered by the server, not here. It is the same function
 * that builds the message handed to ActiveCampaign, so what the preview shows is
 * what goes out; a second renderer in the browser would eventually disagree with
 * the first, and the failure mode is a campaign that proofed clean and sent
 * wrong.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { US_STATES } from '../../constants/usRegions';
import {
  AUDIENCE_SOURCE_LABELS,
  OPT_OUT_LABELS,
  checkCampaignEmails,
  previewAudience,
  previewCampaign,
  sendCampaign,
  sendCampaignTest,
  updateCampaign,
  type AudienceFilter,
  type AudiencePreview,
  type AudienceSource,
  type CampaignOptions,
  type CampaignRow,
  type CampaignUpdate,
  type EmailCheckResult,
  type EmailIssueRecord,
} from '../../api/campaigns';
import EmailCheckModal from './EmailCheckModal';

type Panel = 'content' | 'audience' | 'proof';

interface Props {
  campaign: CampaignRow;
  userId: number;
  options: CampaignOptions;
  onClose: () => void;
  /** Called with the saved row so the list behind the modal stays current. */
  onSaved: (campaign: CampaignRow) => void;
}

const CampaignComposer: React.FC<Props> = ({ campaign, userId, options, onClose, onSaved }) => {
  const isSms = campaign.channel === 'sms';
  const [panel, setPanel] = useState<Panel>('content');

  const [name, setName] = useState(campaign.name);
  const [subject, setSubject] = useState(campaign.subject);
  const [preheader, setPreheader] = useState(campaign.preheader);
  const [fromName, setFromName] = useState(campaign.from_name || options.defaults.fromName);
  const [fromEmail, setFromEmail] = useState(campaign.from_email || options.defaults.fromEmail);
  const [replyTo, setReplyTo] = useState(campaign.reply_to || options.defaults.replyTo);
  const [templateKey, setTemplateKey] = useState(campaign.template_key);
  const [templateData, setTemplateData] = useState<Record<string, string>>(
    campaign.template_data ?? {}
  );
  const [smsBody, setSmsBody] = useState(campaign.sms_body);
  const [filter, setFilter] = useState<AudienceFilter>(() => {
    const stored = campaign.audience_filter;
    const usePortal = stored.usePortal ?? stored.mode === 'portal';
    const useAcList = stored.useAcList ?? stored.mode !== 'portal';
    return {
      ...stored,
      useAcList,
      usePortal,
      mode: useAcList && usePortal ? 'both' : usePortal ? 'portal' : 'ac_list',
      excludedEmails: stored.excludedEmails ?? [],
      addedRecipients: stored.addedRecipients ?? [],
      states: stored.states ?? [],
    };
  });
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [scheduledFor, setScheduledFor] = useState(
    campaign.scheduled_for ? campaign.scheduled_for.slice(0, 16) : ''
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [audience, setAudience] = useState<AudiencePreview | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [preparing, setPreparing] = useState<'' | 'checking' | 'counting'>('');
  const [emailCheck, setEmailCheck] = useState<{
    result: EmailCheckResult;
    purpose: 'send' | 'review';
  } | null>(null);
  /** Possible typos the sender confirmed are right, so later checks in this session skip them. */
  const [keptEmails, setKeptEmails] = useState<string[]>([]);
  /** Bumped when a correction changes records the audience is drawn from. */
  const [audienceVersion, setAudienceVersion] = useState(0);

  const template = useMemo(
    () => options.templates.find((t) => t.key === templateKey) ?? options.templates[0],
    [options.templates, templateKey]
  );

  const payload = useCallback(
    (): CampaignUpdate => ({
      name,
      subject,
      preheader,
      from_name: fromName,
      from_email: fromEmail,
      reply_to: replyTo,
      template_key: templateKey,
      template_data: templateData,
      sms_body: smsBody,
      filter,
      scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
    }),
    [
      name, subject, preheader, fromName, fromEmail, replyTo,
      templateKey, templateData, smsBody, filter, scheduledFor,
    ]
  );

  const save = useCallback(async (): Promise<CampaignRow | null> => {
    setSaving(true);
    setError('');
    try {
      const saved = await updateCampaign(campaign.id, userId, payload());
      onSaved(saved);
      return saved;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [campaign.id, userId, payload, onSaved]);

  // Audience and preview both re-resolve on a debounce, so the counts and the
  // rendering track what is on screen without a request per keystroke.
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (panel !== 'audience') return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setAudienceLoading(true);
      previewAudience(userId, filter, campaign.channel)
        .then(setAudience)
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed to resolve the audience.'))
        .finally(() => setAudienceLoading(false));
    }, 350);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [panel, filter, userId, campaign.channel, audienceVersion]);

  useEffect(() => {
    if (panel !== 'proof' || isSms) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setPreviewLoading(true);
      previewCampaign(campaign.id, userId, {
        template_key: templateKey,
        template_data: templateData,
        preheader,
      })
        .then((p) => {
          setPreviewHtml(p.html ?? '');
          setPreviewErrors(p.errors ?? []);
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed to render the preview.'))
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [panel, isSms, campaign.id, userId, templateKey, templateData, preheader]);

  const toggleSource = (source: AudienceSource) => {
    setFilter((f) => ({
      ...f,
      sources: f.sources.includes(source)
        ? f.sources.filter((s) => s !== source)
        : [...f.sources, source],
    }));
  };

  const toggleInList = (key: 'industries' | 'states', value: string) => {
    setFilter((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));
  };

  const handleTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    setError('');
    setNotice('');
    try {
      if (!(await save())) return;
      const { sentTo } = await sendCampaignTest(campaign.id, userId, testEmail.trim());
      setNotice(`Test copy sent to ${sentTo}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send the test copy.');
    } finally {
      setTesting(false);
    }
  };

  const checkEmails = async (): Promise<{ result: EmailCheckResult; kept: number } | null> => {
    setPreparing('checking');
    setError('');
    setNotice('');
    try {
      const result = await checkCampaignEmails(userId, filter);
      const issues = result.issues.filter((i) => !(i.kind === 'typo' && keptEmails.includes(i.email)));
      return { result: { ...result, issues }, kept: result.issues.length - issues.length };
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to check the email addresses.');
      return null;
    } finally {
      setPreparing('');
    }
  };

  const handleReviewEmails = async () => {
    const checked = await checkEmails();
    if (!checked) return;
    const { result, kept } = checked;
    if (result.issues.length > 0) {
      setEmailCheck({ result, purpose: 'review' });
      return;
    }
    const n = result.checked;
    const parts = [
      n === 0
        ? 'There are no email addresses in this audience yet.'
        : `${n === 1 ? 'The one address looks' : `All ${n.toLocaleString()} addresses look`} deliverable${
            kept ? `, apart from ${kept} you chose to keep as typed` : ''
          }.`,
    ];
    if (result.unchecked > 0) {
      parts.push(
        `${result.unchecked} could not have ${result.unchecked === 1 ? 'its' : 'their'} domain looked up just now, so only the spelling was checked.`
      );
    }
    if (result.truncated) parts.push('Only the first 2,000 contacts of the ActiveCampaign list were checked.');
    setNotice(parts.join(' '));
  };

  const removeEmails = (emails: string[]) =>
    setFilter((f) => ({
      ...f,
      excludedEmails: [...f.excludedEmails, ...emails.filter((e) => !f.excludedEmails.includes(e))],
    }));

  const restoreEmail = (email: string) =>
    setFilter((f) => ({ ...f, excludedEmails: f.excludedEmails.filter((e) => e !== email) }));

  const applyCorrection = (from: string, to: string, records: EmailIssueRecord[]) => {
    setFilter((f) => {
      const renamed = records.some((r) => r.source === 'added')
        ? f.addedRecipients.map((r) => (r.email === from ? { ...r, email: to } : r))
        : f.addedRecipients;
      return {
        ...f,
        excludedEmails: f.excludedEmails.filter((e) => e !== to),
        addedRecipients: renamed.filter((r, i) => renamed.findIndex((x) => x.email === r.email) === i),
      };
    });
    setAudienceVersion((v) => v + 1);
  };

  const keepEmail = (email: string, keep: boolean) =>
    setKeptEmails((list) => [...list.filter((e) => e !== email), ...(keep ? [email] : [])]);

  const handleSend = async () => {
    if (!isSms) {
      const checked = await checkEmails();
      if (!checked) return;
      if (checked.result.issues.length > 0) {
        setEmailCheck({ result: checked.result, purpose: 'send' });
        return;
      }
    }
    await confirmAndSend();
  };

  const confirmAndSend = async () => {
    let count = audience?.total ?? campaign.audience_count;
    if (!isSms) {
      setPreparing('counting');
      count = await previewAudience(userId, filter, campaign.channel)
        .then((p) => p.total)
        .catch(() => count);
      setPreparing('');
    }
    const when = scheduledFor ? `scheduled for ${new Date(scheduledFor).toLocaleString()}` : 'sent now';
    if (
      !window.confirm(
        `Send "${name}" to ${count} recipient${count === 1 ? '' : 's'}?\n\nThis will be ${when} and cannot be undone once ActiveCampaign starts delivering.`
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (!(await save())) return;
      onSaved(await sendCampaign(campaign.id, userId));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start the send.');
    } finally {
      setSaving(false);
    }
  };

  const smsChars = smsBody.length;

  const smsBlocker = isSms ? options.smsBlocker : null;

  return (
    <section className="campaign-editor" aria-labelledby="campaign-editor-heading">
      <div className="campaign-editor-bar">
        <button type="button" className="secondary-button" onClick={onClose}>
          Back to campaigns
        </button>
        <h2 className="events-section-title" id="campaign-editor-heading">
          Edit campaign — {name || campaign.name}
        </h2>
      </div>
      <div className="campaign-composer">
        <div className="campaign-panel-tabs" role="tablist" aria-label="Composer sections">
          {([
            ['content', isSms ? 'Message' : 'Content'],
            ['audience', 'Audience'],
            ['proof', isSms ? 'Review & send' : 'Preview & send'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={panel === id}
              className={`campaign-panel-tab${panel === id ? ' campaign-panel-tab--active' : ''}`}
              onClick={() => setPanel(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <p className="create-order-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="campaign-notice">{notice}</p> : null}
        {smsBlocker ? (
          <div className="campaign-setup-banner" role="status">
            <strong>This text campaign cannot send yet.</strong>
            <p>{smsBlocker}</p>
          </div>
        ) : null}

        {panel === 'content' ? (
          <div className="campaign-panel">
            <label className="form-label" htmlFor="campaign-name">
              Campaign name
            </label>
            <input
              id="campaign-name"
              className="form-input"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="campaign-hint">Internal only — recipients never see this.</p>

            {isSms ? (
              <>
                <label className="form-label" htmlFor="campaign-sms">
                  Message
                </label>
                <textarea
                  id="campaign-sms"
                  className="form-input campaign-textarea"
                  rows={5}
                  maxLength={1600}
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                />
                <p className="campaign-hint">
                  {smsChars} characters. Carriers bill per 160-character segment, and a single
                  curly quote or emoji drops that to 70. Keep an opt-out such as “Reply STOP to
                  unsubscribe”.
                </p>
              </>
            ) : (
              <>
                <div className="campaign-field-row">
                  <div>
                    <label className="form-label" htmlFor="campaign-subject">
                      Subject line
                    </label>
                    <input
                      id="campaign-subject"
                      className="form-input"
                      value={subject}
                      maxLength={300}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="campaign-preheader">
                      Preview text
                    </label>
                    <input
                      id="campaign-preheader"
                      className="form-input"
                      value={preheader}
                      maxLength={300}
                      onChange={(e) => setPreheader(e.target.value)}
                    />
                  </div>
                </div>
                <p className="campaign-hint">
                  Preview text is the grey line an inbox shows after the subject.
                </p>

                <div className="campaign-field-row">
                  <div>
                    <label className="form-label" htmlFor="campaign-from-name">
                      From name
                    </label>
                    <input
                      id="campaign-from-name"
                      className="form-input"
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="campaign-from-email">
                      From address
                    </label>
                    <input
                      id="campaign-from-email"
                      className="form-input"
                      type="email"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                    />
                  </div>
                </div>
                <p className="campaign-hint">
                  The from address has to be on a domain authenticated in ActiveCampaign.
                  Anything else is rejected outright, including test sends.
                </p>

                <label className="form-label" htmlFor="campaign-reply-to">
                  Reply-to
                </label>
                <input
                  id="campaign-reply-to"
                  className="form-input"
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
                <p className="campaign-hint">Where replies land. Often a monitored inbox rather than the from address.</p>

                <label className="form-label" htmlFor="campaign-template">
                  Layout
                </label>
                <select
                  id="campaign-template"
                  className="form-input"
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                >
                  {options.templates.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {template ? <p className="campaign-hint">{template.description}</p> : null}

                {template?.fields.map((f) => (
                  <div key={f.key} className="campaign-template-field">
                    <label className="form-label" htmlFor={`tpl-${f.key}`}>
                      {f.label}
                      {f.required ? ' *' : ''}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea
                        id={`tpl-${f.key}`}
                        className="form-input campaign-textarea"
                        rows={8}
                        maxLength={f.maxLength}
                        value={templateData[f.key] ?? ''}
                        onChange={(e) =>
                          setTemplateData((d) => ({ ...d, [f.key]: e.target.value }))
                        }
                      />
                    ) : (
                      <input
                        id={`tpl-${f.key}`}
                        className="form-input"
                        type={f.type === 'url' ? 'url' : 'text'}
                        maxLength={f.maxLength}
                        value={templateData[f.key] ?? ''}
                        onChange={(e) =>
                          setTemplateData((d) => ({ ...d, [f.key]: e.target.value }))
                        }
                      />
                    )}
                    {f.help ? <p className="campaign-hint">{f.help}</p> : null}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : null}

        {panel === 'audience' ? (
          <div className="campaign-panel">
            {!isSms ? (
              <>
                <fieldset className="campaign-fieldset">
                  <legend>Who to send to</legend>
                  <label className="campaign-checkbox campaign-checkbox--block">
                    <input
                      type="checkbox"
                      checked={filter.useAcList}
                      onChange={(e) =>
                        setFilter((f) => {
                          const useAcList = e.target.checked;
                          const usePortal = f.usePortal;
                          return {
                            ...f,
                            useAcList,
                            mode: useAcList && usePortal ? 'both' : usePortal ? 'portal' : 'ac_list',
                          };
                        })
                      }
                    />
                    An ActiveCampaign list
                  </label>
                  <label className="campaign-checkbox campaign-checkbox--block">
                    <input
                      type="checkbox"
                      checked={filter.usePortal}
                      onChange={(e) =>
                        setFilter((f) => {
                          const usePortal = e.target.checked;
                          const useAcList = f.useAcList;
                          return {
                            ...f,
                            usePortal,
                            mode: useAcList && usePortal ? 'both' : usePortal ? 'portal' : 'ac_list',
                          };
                        })
                      }
                    />
                    Build an audience from portal data
                  </label>
                </fieldset>
                <p className="campaign-hint">
                  Either source can be used on its own, or both. People who appear in both are
                  emailed once.
                </p>

                {filter.useAcList ? (
                  <>
                    <label className="form-label" htmlFor="campaign-ac-list">
                      ActiveCampaign list
                    </label>
                    <select
                      id="campaign-ac-list"
                      className="form-input"
                      value={filter.acListId ?? ''}
                      onChange={(e) =>
                        setFilter((f) => ({ ...f, acListId: Number(e.target.value) || undefined }))
                      }
                    >
                      <option value="">Choose a list…</option>
                      {options.emailLists.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({l.contacts} contacts)
                        </option>
                      ))}
                    </select>
                    <p className="campaign-hint">
                      These contacts stay where they opted in. Anyone who unsubscribed from this
                      list, or from any other campaign, or whose address bounced, is left out.
                    </p>
                  </>
                ) : null}

                {filter.usePortal ? (
                  <div className="campaign-portal-audience">
                    <p className="campaign-consent-note">
                      Portal records are added to the send. Those people have not necessarily
                      opted in to marketing, so make sure there is a basis for contacting them.
                      Anyone who has unsubscribed from a past campaign is left out automatically.
                    </p>
                  </div>
                ) : null}
              </>
            ) : null}

            {isSms ? (
              <>
                <label className="form-label" htmlFor="campaign-sms-list">
                  ActiveCampaign SMS list
                </label>
                <select
                  id="campaign-sms-list"
                  className="form-input"
                  value={filter.smsListId ?? ''}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, smsListId: Number(e.target.value) || undefined }))
                  }
                >
                  <option value="">Choose a list…</option>
                  {options.smsLists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.contacts} subscribers)
                    </option>
                  ))}
                </select>
                <p className="campaign-hint">
                  Text broadcasts go to a list maintained inside ActiveCampaign rather than one
                  built from portal data. Texting needs recorded consent from the person, and the
                  portal's intake forms don't collect it — so the subscribers stay where that
                  consent was actually gathered.
                </p>
              </>
            ) : filter.usePortal ? (
              <>
                <fieldset className="campaign-fieldset">
                  <legend>Where to draw from</legend>
                  {(Object.keys(AUDIENCE_SOURCE_LABELS) as AudienceSource[]).map((source) => (
                    <label key={source} className="campaign-checkbox">
                      <input
                        type="checkbox"
                        checked={filter.sources.includes(source)}
                        onChange={() => toggleSource(source)}
                      />
                      {AUDIENCE_SOURCE_LABELS[source]}
                    </label>
                  ))}
                </fieldset>

                {filter.sources.includes('client') ? (
                  <fieldset className="campaign-fieldset">
                    <legend>Client records</legend>
                    {(['client', 'lead'] as const).map((t) => (
                      <label key={t} className="campaign-checkbox">
                        <input
                          type="checkbox"
                          checked={filter.clientTypes.includes(t)}
                          onChange={() =>
                            setFilter((f) => ({
                              ...f,
                              clientTypes: f.clientTypes.includes(t)
                                ? f.clientTypes.filter((v) => v !== t)
                                : [...f.clientTypes, t],
                            }))
                          }
                        />
                        {t === 'client' ? 'Clients' : 'Leads'}
                      </label>
                    ))}
                  </fieldset>
                ) : null}

                {options.industries.length > 0 ? (
                  <fieldset className="campaign-fieldset">
                    <legend>Industry</legend>
                    {options.industries.map((i) => (
                      <label key={i} className="campaign-checkbox">
                        <input
                          type="checkbox"
                          checked={filter.industries.includes(i)}
                          onChange={() => toggleInList('industries', i)}
                        />
                        {i}
                      </label>
                    ))}
                  </fieldset>
                ) : null}

                <label className="form-label" htmlFor="campaign-state">
                  State
                </label>
                <select
                  id="campaign-state"
                  className="form-input"
                  value=""
                  onChange={(e) => {
                    const code = e.target.value;
                    if (!code) return;
                    setFilter((f) =>
                      f.states.includes(code) ? f : { ...f, states: [...f.states, code] }
                    );
                  }}
                >
                  <option value="">Add a state…</option>
                  {US_STATES.filter((s) => !filter.states.includes(s.code)).map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
                {filter.states.length > 0 ? (
                  <ul className="campaign-state-pills">
                    {filter.states.map((code) => (
                      <li key={code}>
                        {US_STATES.find((s) => s.code === code)?.name ?? code}
                        <button
                          type="button"
                          aria-label={`Remove ${code}`}
                          onClick={() =>
                            setFilter((f) => ({ ...f, states: f.states.filter((s) => s !== code) }))
                          }
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="campaign-hint">No state selected, so every state is included.</p>
                )}

                <label className="campaign-checkbox campaign-checkbox--block">
                  <input
                    type="checkbox"
                    checked={filter.excludeConverted}
                    onChange={(e) =>
                      setFilter((f) => ({ ...f, excludeConverted: e.target.checked }))
                    }
                  />
                  Skip intake records that already became a client
                </label>

                <label className="form-label" htmlFor="campaign-search">
                  Narrow by name, company or email
                </label>
                <input
                  id="campaign-search"
                  className="form-input"
                  value={filter.search}
                  onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                />

              </>
            ) : null}

            {!isSms ? (
            <div className="campaign-audience-result">
              {audienceLoading ? (
                <p className="page-subtitle">Resolving…</p>
              ) : audience ? (
                <>
                  <p className="campaign-audience-count">
                    <strong>{audience.total}</strong> recipient
                    {audience.total === 1 ? '' : 's'}
                    {audience.total > 0 ? ', de-duplicated by email address' : ''}
                  </p>
                  <div className="campaign-email-check-row">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => void handleReviewEmails()}
                      disabled={preparing !== '' || saving}
                    >
                      {preparing === 'checking' ? 'Checking addresses…' : 'Check email addresses'}
                    </button>
                    <span className="campaign-email-check-hint">
                      Finds typos and addresses that would bounce. Sending runs the same check first.
                    </span>
                  </div>
                  <div className="campaign-add-recipient">
                    <input
                      className="form-input"
                      type="email"
                      placeholder="Add an email address"
                      aria-label="Email address to add"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                    />
                    <input
                      className="form-input"
                      placeholder="Name (optional)"
                      aria-label="Name to add"
                      value={addName}
                      onChange={(e) => setAddName(e.target.value)}
                    />
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        const email = addEmail.trim().toLowerCase();
                        if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(email)) return;
                        const [firstName, ...rest] = addName.trim().split(/\s+/);
                        setFilter((f) => ({
                          ...f,
                          excludedEmails: f.excludedEmails.filter((e) => e !== email),
                          addedRecipients: f.addedRecipients.some((r) => r.email === email)
                            ? f.addedRecipients
                            : [
                                ...f.addedRecipients,
                                { email, firstName: firstName ?? '', lastName: rest.join(' ') },
                              ],
                        }));
                        setAddEmail('');
                        setAddName('');
                      }}
                    >
                      Add
                    </button>
                  </div>
                  {audience.sample.length > 0 ? (
                    <ul className="campaign-recipient-list">
                      {audience.sample.map((m) => (
                        <li key={m.email}>
                          <span>
                            {m.email}
                            {m.company ? ` — ${m.company}` : ''}
                            {m.firstName || m.lastName
                              ? ` (${[m.firstName, m.lastName].filter(Boolean).join(' ')})`
                              : ''}
                          </span>
                          <button
                            type="button"
                            className="campaign-recipient-remove"
                            onClick={() =>
                              setFilter((f) => ({
                                ...f,
                                excludedEmails: f.excludedEmails.includes(m.email.toLowerCase())
                                  ? f.excludedEmails
                                  : [...f.excludedEmails, m.email.toLowerCase()],
                                addedRecipients: f.addedRecipients.filter(
                                  (r) => r.email !== m.email.toLowerCase()
                                ),
                              }))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="campaign-hint">No recipients yet. Add one above, or widen the audience.</p>
                  )}
                  {audience.leftOutTotal ? (
                    <details className="campaign-left-out">
                      <summary>
                        {audience.leftOutTotal} {audience.leftOutTotal === 1 ? 'person was' : 'people were'}{' '}
                        left out because they opted out of marketing email
                      </summary>
                      <ul className="campaign-recipient-list">
                        {(audience.leftOut ?? []).map((p) => (
                          <li key={p.email}>
                            <span>{p.email}</span>
                            <span className="campaign-left-out-reason">{OPT_OUT_LABELS[p.reason]}</span>
                          </li>
                        ))}
                      </ul>
                      {audience.leftOutTotal > (audience.leftOut?.length ?? 0) ? (
                        <p className="campaign-hint">
                          Showing the first {audience.leftOut?.length}.
                        </p>
                      ) : null}
                    </details>
                  ) : null}
                </>
              ) : null}
            </div>
            ) : null}
          </div>
        ) : null}

        {panel === 'proof' ? (
          <div className="campaign-panel">
            {previewErrors.length > 0 ? (
              <div className="campaign-blockers" role="alert">
                <strong>Not ready to send:</strong>
                <ul>
                  {previewErrors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {isSms ? (
              <div className="campaign-sms-preview">
                <p className="campaign-sms-bubble">{smsBody || 'Nothing written yet.'}</p>
              </div>
            ) : (
              <div className="campaign-preview-frame">
                {previewLoading ? (
                  <p className="page-subtitle">Rendering…</p>
                ) : (
                  <iframe
                    title="Campaign preview"
                    className="campaign-preview-iframe"
                    // Sandboxed with no allowed capabilities: the preview is
                    // generated markup and has no reason to run anything.
                    sandbox=""
                    srcDoc={previewHtml}
                  />
                )}
              </div>
            )}

            {!isSms ? (
              <div className="campaign-test-row">
                <label className="form-label" htmlFor="campaign-test-email">
                  Send a proof copy to
                </label>
                <div className="campaign-test-controls">
                  <input
                    id="campaign-test-email"
                    className="form-input"
                    type="email"
                    placeholder="you@techforcerobotics.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={testing || !testEmail.trim()}
                    onClick={handleTest}
                  >
                    {testing ? 'Sending…' : 'Send proof'}
                  </button>
                </div>
                <p className="campaign-hint">
                  A proof goes out over the portal's own mail server, so it shows the layout and
                  the copy but not the merge tags, tracked links, or the unsubscribe footer —
                  ActiveCampaign adds those when the real campaign sends.
                </p>
              </div>
            ) : null}

            <label className="form-label" htmlFor="campaign-schedule">
              Schedule for later (optional)
            </label>
            <input
              id="campaign-schedule"
              className="form-input"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
            <p className="campaign-hint">
              Leave empty to send as soon as you press Send.
            </p>
          </div>
        ) : null}

        <div className="modal-actions campaign-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Close
          </button>
          <button type="button" className="secondary-button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={handleSend}
            disabled={saving || preparing !== '' || previewErrors.length > 0 || Boolean(smsBlocker)}
          >
            {preparing === 'checking'
              ? 'Checking addresses…'
              : preparing === 'counting'
                ? 'Counting recipients…'
                : scheduledFor
                  ? 'Schedule'
                  : 'Send now'}
          </button>
        </div>
      </div>
      {emailCheck ? (
        <EmailCheckModal
          userId={userId}
          result={emailCheck.result}
          purpose={emailCheck.purpose}
          onRemove={removeEmails}
          onRestore={restoreEmail}
          onCorrected={applyCorrection}
          onKeep={keepEmail}
          onContinue={() => {
            setEmailCheck(null);
            void confirmAndSend();
          }}
          onClose={() => setEmailCheck(null)}
        />
      ) : null}
    </section>
  );
};

export default CampaignComposer;
